import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../../Utils/axiosInstance";
import toast from "react-hot-toast";
import { s3Upload } from "../../../Utils/s3Upload";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";

const ConfirmRefundReceiptModal = ({ isOpen, onClose, refund, onSuccess }) => {
  const [animate, setAnimate] = useState(false);
  const [phase, setPhase] = useState("confirm"); // confirm | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [remarks, setRemarks] = useState("");
  const [proofFiles, setProofFiles] = useState([]); // { file, previewUrl, uploadUrl, uploading }
  const [successPop, setSuccessPop] = useState(false);
  const fileInputRef = useRef(null);

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      setPhase("confirm");
      setErrorMessage("");
      setRemarks("");
      setProofFiles([]);
      setSuccessPop(false);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (phase === "success") {
      setSuccessPop(false);
      const t = setTimeout(() => setSuccessPop(true), 20);
      return () => clearTimeout(t);
    }
  }, [phase]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handlePickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setProofFiles((prev) => [
      ...prev,
      ...files.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        uploadUrl: null,
        uploading: false,
      })),
    ]);
    e.target.value = "";
  };

  const handleUploadFile = async (idx) => {
    const item = proofFiles[idx];
    if (!item || item.uploadUrl || item.uploading) return;

    setProofFiles((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], uploading: true };
      return copy;
    });

    try {
      const result = await s3Upload(item.file, "refunds");
      setProofFiles((prev) => {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], uploadUrl: result.url, uploading: false };
        return copy;
      });
      toast.success("File uploaded");
    } catch (err) {
      setProofFiles((prev) => {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], uploading: false };
        return copy;
      });
      toast.error(err.message || "Failed to upload file");
    }
  };

  const handleRemoveFile = (idx) => {
    setProofFiles((prev) => prev.filter((_, i) => i !== idx));
  };
 
  const canSubmit = proofFiles.some((f) => f.uploadUrl) || remarks.trim().length > 0;
  const hasUnuploadedFiles = proofFiles.some((f) => !f.uploadUrl);

  const handleConfirm = async () => {
    const uploadedUrls = proofFiles.filter((f) => f.uploadUrl).map((f) => f.uploadUrl);
    try {
      setPhase("loading");
      const { data } = await axios.patch(
        `/refund/buyer/${refund._id}/confirm-receipt`,
        {
          proofUrls: uploadedUrls.length ? uploadedUrls : undefined,
          remarks: remarks.trim() || undefined,
        }
      );
      if (data?.success) {
        setPhase("success");
        onSuccess?.();
      } else {
        setErrorMessage(data?.message || "Failed to confirm receipt");
        setPhase("error");
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Something went wrong");
      setPhase("error");
    }
  };

  const handleRetry = () => {
    setErrorMessage("");
    setPhase("confirm");
  };

  if (!isOpen || !refund) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
      bg-black/30 backdrop-blur-sm transition-opacity duration-300
      ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        className={`bg-white w-full md:w-[480px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
        ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex-1 overflow-auto p-6">
          {phase === "confirm" && (
            <>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                  <span className="text-3xl">✅</span>
                </div>
                <h2 className="text-lg font-bold text-gray-800">Confirm Refund Receipt</h2>
                <p className="text-xl font-bold text-emerald-600 mt-2">
                  ₹{Number(refund.refundAmount || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1.5">
                  Confirm that you have received this refund amount.
                </p>
              </div>

              <div className="h-px bg-gray-100 my-5" />

              <div className="mb-5">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  Refund Details
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Transaction ID</span>
                    <span className="font-semibold font-mono text-gray-800">
                      {refund.transactionId || "—"}
                    </span>
                  </div>
                  {refund.transactionDetails?.method && (
                    <div className="flex justify-between text-xs pt-2 border-t border-gray-100">
                      <span className="text-gray-500">Method</span>
                      <span className="font-semibold text-gray-800">
                        {refund.transactionDetails.method.replace(/_/g, " ")}
                      </span>
                    </div>
                  )}
                  {(refund.processedAt || refund.processedDate) && (
                    <div className="flex justify-between text-xs pt-2 border-t border-gray-100">
                      <span className="text-gray-500">Processed</span>
                      <span className="font-semibold text-gray-800">
                        {new Date(refund.processedAt || refund.processedDate).toLocaleDateString(
                          "en-IN",
                          { day: "numeric", month: "short", year: "numeric" }
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-5">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  Proof (Optional)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  className="hidden"
                  onChange={handlePickFiles}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-200 rounded-xl py-4 flex items-center justify-center gap-2 text-sm font-semibold text-gray-500 hover:border-primaryColor/40 hover:text-primaryColor transition-colors"
                >
                  <span>📎</span> Add Proof Document
                </button>

                {proofFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {proofFiles.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {item.file.type?.startsWith("image/") ? (
                            <img
                              src={item.previewUrl}
                              alt=""
                              className="w-8 h-8 rounded object-cover flex-shrink-0"
                            />
                          ) : (
                            <span className="text-lg">📄</span>
                          )}
                          <span
                            className={`text-xs font-medium truncate ${item.uploadUrl ? "text-emerald-600" : "text-gray-600"
                              }`}
                          >
                            {item.file.name}
                          </span>
                        </div>

                        {item.uploadUrl ? (
                          <span className="text-emerald-500 text-lg flex-shrink-0">✓</span>
                        ) : item.uploading ? (
                          <div className="w-4 h-4 border-2 border-primaryColor border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        ) : (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => handleUploadFile(idx)}
                              className="text-primaryColor text-xs font-bold"
                            >
                              Upload
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(idx)}
                              className="text-red-500 text-xs font-bold"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-2">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  Remarks (Optional)
                </p>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value.slice(0, 300))}
                  rows={3}
                  placeholder="Add any additional notes..."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primaryColor resize-none"
                />
                <p className="text-[10px] text-gray-400 text-right mt-1">{remarks.length}/300</p>
              </div>
            </>
          )}

          {phase === "loading" && (
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 rounded-full bg-primaryColor/10 flex items-center justify-center mb-4">
                <div className="w-7 h-7 border-[3px] border-primaryColor border-t-transparent rounded-full animate-spin" />
              </div>
              <h2 className="text-lg font-bold text-gray-800">Submitting</h2>
              <p className="text-xs text-gray-500 mt-1.5">
                Please wait while we confirm your refund receipt...
              </p>
            </div>
          )}

          {phase === "success" && (
            <div className="flex flex-col items-center text-center py-6">
              <div
                className={`w-[72px] h-[72px] rounded-full bg-emerald-50 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20 transition-all duration-500 ease-out
                ${successPop ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}
              >
                <span className="text-4xl">✅</span>
              </div>
              <h2 className="text-lg font-bold text-gray-800">Confirmed! ✓</h2>
              <div className="h-px bg-gray-100 w-full my-4" />
              <p className="text-sm text-gray-500">
                Your refund receipt has been confirmed successfully.
              </p>
              <button
                onClick={handleClose}
                className="mt-5 w-full py-3 rounded-xl bg-primaryColor text-white font-bold text-sm"
              >
                Done
              </button>
            </div>
          )}

          {phase === "error" && (
            <div className="flex flex-col items-center text-center py-6">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <span className="text-3xl">⚠️</span>
              </div>
              <h2 className="text-lg font-bold text-gray-800">Request Failed</h2>
              <div className="h-px bg-gray-100 w-full my-4" />
              <p className="text-sm text-gray-500">{errorMessage}</p>
              <button
                onClick={handleRetry}
                className="mt-5 w-full py-3 rounded-xl bg-red-500 text-white font-bold text-sm"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {phase === "confirm" && (
          <div className="p-5 border-t flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canSubmit || hasUnuploadedFiles}
              className="flex-1 py-3 bg-primaryColor text-white rounded-2xl font-semibold text-sm disabled:opacity-50"
            >
              Confirm
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ConfirmRefundReceiptModal;